import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Send, Paperclip, X, FileText, Image as ImageIcon, CheckCircle2, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";

export const CAT_LABELS = {
  billing: "Billing", technical: "Technical issue", account: "Account",
  content: "Content question", other: "Other",
};
const STATUS_STYLES = {
  open: "text-primary border-primary/50",
  answered: "text-emerald-400 border-emerald-500/40",
  closed: "text-muted-foreground border-border",
};

export function SupportThread({ ticketId, isAdmin = false, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef();
  const endRef = useRef();

  const load = () => {
    setLoading(true);
    api.get(`/support/tickets/${ticketId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load ticket"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticketId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const send = async () => {
    if (!reply.trim() && !file) return;
    setSending(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/support/upload", fd);
        attachment_id = up.data.id;
      }
      await api.post(`/support/tickets/${ticketId}/messages`, { body: reply.trim(), attachment_id });
      setReply(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send message");
    } finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try { await api.patch(`/support/tickets/${ticketId}`, { status }); load(); onChanged?.(); }
    catch { toast.error("Could not update status"); }
  };

  const openAttachment = async (a) => {
    try {
      const res = await api.get(`/support/files/${a.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch { toast.error("Could not open attachment"); }
  };

  if (loading || !data)
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;

  const t = data.ticket;
  const closed = t.status === "closed";

  return (
    <div className="flex flex-col h-full" data-testid="support-thread">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
        <div className="min-w-0">
          <h2 className="font-serif text-2xl leading-tight truncate">{t.subject}</h2>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status] || ""}`} data-testid="support-status">{t.status}</span>
            <span className="text-xs text-muted-foreground font-mono">{CAT_LABELS[t.category] || "Other"}</span>
            {isAdmin && <span className="text-xs text-muted-foreground">· {t.user_name} ({t.user_email})</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {closed ? (
            <button onClick={() => setStatus("open")} data-testid="support-reopen"
              className="flex items-center gap-1.5 border border-border px-3 py-1.5 rounded-sm text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          ) : (
            <button onClick={() => setStatus("closed")} data-testid="support-close"
              className="flex items-center gap-1.5 border border-border px-3 py-1.5 rounded-sm text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-5 space-y-4" data-testid="support-messages">
        {data.messages.map((m) => {
          const mine = isAdmin ? m.sender_role === "admin" : m.sender_role === "user";
          const fromSupport = m.sender_role === "admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-md px-4 py-3 ${mine ? "bg-primary/15 border border-primary/30" : "bg-muted border border-border"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-widest font-mono ${fromSupport ? "text-primary" : "text-muted-foreground"}`}>
                    {fromSupport ? "Academy Support" : (isAdmin ? m.sender_name : "You")}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
                </div>
                {m.body && <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                {m.attachment && (
                  <button onClick={() => openAttachment(m.attachment)} data-testid="support-attachment"
                    className="mt-2 inline-flex items-center gap-2 text-xs border border-border rounded-sm px-2.5 py-1.5 hover:border-primary hover:text-primary transition-colors">
                    {m.attachment.is_image ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                    <span className="truncate max-w-[180px]">{m.attachment.filename}</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      {closed ? (
        <div className="border-t border-border pt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="w-4 h-4" /> This ticket is resolved. Reopen it to continue the conversation.
        </div>
      ) : (
        <div className="border-t border-border pt-4">
          {file && (
            <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
              <Paperclip className="w-3.5 h-3.5" /> <span className="truncate max-w-[220px]">{file.name}</span>
              <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X className="w-3.5 h-3.5 hover:text-destructive-foreground" /></button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea data-testid="support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
              rows={2} placeholder="Write a reply…  (⌘/Ctrl + Enter to send)"
              className="flex-1 bg-muted border border-input rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary resize-none" />
            <input ref={fileRef} type="file" className="hidden" data-testid="support-file-input"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button onClick={() => fileRef.current?.click()} data-testid="support-attach-btn" title="Attach a file"
              className="w-10 h-10 flex items-center justify-center border border-border rounded-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <Paperclip className="w-4 h-4" />
            </button>
            <button onClick={send} disabled={sending || (!reply.trim() && !file)} data-testid="support-send-btn"
              className="w-10 h-10 flex items-center justify-center bg-primary text-primary-foreground rounded-sm disabled:opacity-40 hover:-translate-y-[1px] transition-transform">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportThread;
